import { Injectable, OnDestroy } from '@angular/core';
import {
    BehaviorSubject,
    combineLatest,
    filter,
    first,
    map,
    mergeMap,
    Observable,
    of,
    Subject,
    takeUntil
} from 'rxjs';
import { LgvAPIService } from './lgv-api.service';
import { LgvAuthMgrService } from './lgv-auth-mgr.service';
import { LgvClientService } from './lgv-client.service';
import { LgvModuleService, LgvNavItem } from './lgv-module.service';
import { LgvUserService } from './lgv-user.service';
import { NavigationEnd, Router } from '@angular/router';

@Injectable({
    providedIn: 'root'
})
export class LgvNavBuilderService implements OnDestroy {
    private currentUrl = '';
    activeModule$ = new BehaviorSubject(RFModule.Default);

    preLicenseNavLinks$ = new BehaviorSubject([] as LgvNavItem[]);
    currentNavigation$ = new BehaviorSubject([] as LgvNavItem[]);
    clientNavigation$ = new BehaviorSubject([] as LgvNavItem[]);
    clientNavigationFullPath$ = new BehaviorSubject([] as LgvNavItem[]);
    agentAdminNavigationFullPath$ = new BehaviorSubject([] as LgvNavItem[]);

    // convert url to include 'client/'
    get clientNavigationSideMenu$(): Observable<LgvNavItem[]> {
        return this.clientNavigation$.pipe(
            map(array => {
                return array?.map(navItem => {
                    return { ...navItem, link: 'client/' + navItem.link };
                });
            })
        );
    }

    onDestroy$ = new Subject();

    NonAdminNavLinks = [
        {
            id: 'activities',
            link: 'admin-activities',
            label: 'Activities',
            roles: ['Admin']
        },
        { id: 'agents', link: 'agents', label: 'Agents', roles: ['Admin'] },
        {
            id: 'client-details',
            link: '',
            label: 'Client Details',
            roles: ['Admin'],
            hidden: true
        }
    ];

    constructor(
        private lgvAuthMgrSvc: LgvAuthMgrService,
        private lgvApiSvc: LgvAPIService,
        private router: Router,
        private lgvClientSvc: LgvClientService,
        private lgvUserSvc: LgvUserService,
        private lgvModuleSvc: LgvModuleService
    ) {
        this.buildClientNavigation();
        this.buildAgentAdminNavigation();

        router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(({ urlAfterRedirects }: NavigationEnd) => {
                this.currentUrl = urlAfterRedirects;
                this.updateNavigation();
            });
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    updateNavigation() {
        if (this.currentUrl.startsWith('/client/')) {
            this.currentNavigation$.next(this.clientNavigationFullPath$.value);
            this.activeModule$.next(RFModule.Client);
        } else if (this.currentUrl.startsWith('/admin/')) {
            this.preLicenseNavLinks$.next(
                this.agentAdminNavigationFullPath$.value.filter(
                    x => !!x?.isPreLicensed
                )
            );
            this.currentNavigation$.next(
                this.agentAdminNavigationFullPath$.value.filter(
                    x => !x?.isPreLicensed
                )
            );
            this.activeModule$.next(RFModule.Admin);
        } else {
            this.activeModule$.next(RFModule.Default);
        }
    }

    buildAgentAdminNavigation() {
        let dbUserSSCopy = null;
        let companyCopy = null;
        let navCopy: LgvNavItem[] = [];

        this.lgvUserSvc.DBUserSS$.pipe(
            first(x => x !== null),
            takeUntil(this.onDestroy$),
            mergeMap(dbUserSS => {
                dbUserSSCopy = dbUserSS;

                // if user is both admin and agent, build the nav menu for admin role only by stripping out the Agent role here...
                const roles = this.lgvUserSvc.DBUserRoles;
                let modifiedRoles;
                if (roles.includes('Admin') && roles.includes('Agent')) {
                    modifiedRoles = roles.filter(r => r !== 'Agent');
                } else if (
                    roles.includes('CompanyAdmin') &&
                    roles.includes('Agent')
                ) {
                    modifiedRoles = ['CompanyAdminAgent'];
                } else {
                    modifiedRoles = roles;
                }

                navCopy = this.lgvModuleSvc.NavPagesForRoles(modifiedRoles);

                return this.lgvUserSvc.Company$.pipe(
                    first(a => a !== null),
                    takeUntil(this.onDestroy$)
                );
            }),
            mergeMap(company => {
                companyCopy = company;
                return this.lgvUserSvc.isOnboarding$;
            })
        ).subscribe(isOnboarding => {
            const buildNav: LgvNavItem[] = [];

            for (let x of navCopy) {
                // exclude co-branding for RE Agents
                if (
                    x.label.toLowerCase() == 'co-branding clients' &&
                    companyCopy.businessTypeName.toLowerCase() !=
                        'mortgage lender'
                ) {
                    continue;
                }
                // exclude Agents tab for onboarding mode
                if (x.label.toLowerCase() == 'agents' && !!isOnboarding) {
                    continue;
                }

                if (x.label.toLowerCase() == 'co-branding clients') {
                    continue;
                }

                if (!x.link.startsWith('/admin/')) {
                    x.link = `/admin/${x.link}`;
                }
                buildNav.push(x);
            }

            for (let navLink of this.NonAdminNavLinks) {
                if (navLink.id === 'agent-profile') {
                    navLink.link = `/admin/view-agent/${dbUserSSCopy.agentInfo.id}/${navLink.id}`;
                }
            }

            this.agentAdminNavigationFullPath$.next(buildNav);
            this.updateNavigation();
        });
    }

    buildClientNavigation() {
        combineLatest([
            this.lgvApiSvc.AgentSS$,
            this.lgvClientSvc.SnapShot$,
            this.lgvClientSvc.clientPersonas$,
            this.lgvClientSvc.SelectedProperty$,
            this.lgvClientSvc.allOpportunities$,
            this.lgvClientSvc.SelectedClientLoan$,
            this.lgvClientSvc.IsEPO$,
            this.lgvUserSvc.AgentIsExcludingRefinanceOptions$,
            this.lgvUserSvc.ExcludedPagesForAgent$,
            this.lgvClientSvc.isBetaClient$
        ])
            .pipe(filter(f => f !== null))

            .subscribe(
                ([
                    agentSS,
                    clientSS,
                    clientPersonas,
                    selectedProperty,
                    opportunities,
                    selectedLoan,
                    isEPO,
                    agentIsExcludingRefinanceOptions,
                    excludeList,
                    isBeta
                ]) => {
                    let buildNav = [];
                    // convert to bool to avoid incorrect evaluation of null
                    const isBetaClient = !!isBeta;
                    if (!!agentSS && !!clientPersonas && !isBetaClient) {
                        // const isPropertyLess = clientPersonas.indexOf('Propertyless') > -1;
                        // const isOwnedProperty = !!selectedProperty && selectedProperty?.associationType === 'Owned';
                        // const haveOpportunities = !!opportunities && opportunities.length > 0;

                        //console.log(`=== isOwned: ${isOwnedProperty}  |  haveOpportunities: ${haveOpportunities}  |  isEPO: ${isEPO}  |  agentIsExcludingRefinanceOptions: ${agentIsExcludingRefinanceOptions} ===`);
                        //console.log('Opportunities:');
                        //console.log(opportunities);

                        buildNav.push({
                            label: 'Overview',
                            link: 'client-overview',
                            icon: 'chart-line',
                            iconStyle: 'solid'
                        });
                    }

                    if (clientSS && clientSS.hasLoanApplication) {
                        buildNav.push({
                            label: 'Loan Applications',
                            link: 'loan-app',
                            roles: [],
                            icon: 'landmark-magnifying-glass',
                            iconStyle: 'solid'
                        });
                    }

                    if (!isBetaClient) {
                        buildNav.push({
                            label: 'Properties',
                            link: 'client-configuration',
                            roles: [],
                            icon: 'house',
                            iconStyle: 'solid'
                        });

                        if (!!opportunities && opportunities.length > 0) {
                            buildNav.push({
                                label: 'Pricing Quotes',
                                link: 'opportunities',
                                icon: 'circle-dollar',
                                iconStyle: 'solid'
                            });
                        } else {
                            buildNav.push({
                                label: 'Pricing Quotes',
                                link: 'rates',
                                icon: 'circle-dollar',
                                iconStyle: 'solid',
                                disabled: true
                            });
                        }

                        buildNav.push({
                            label: 'Education',
                            link: 'education',
                            roles: [],
                            icon: 'calculator-simple',
                            iconStyle: 'regular'
                        });
                        buildNav = buildNav.filter(
                            n => excludeList.indexOf(n.link) === -1
                        );
                    }

                    // console.log('New Client Navigation:');
                    // console.log(buildNav);
                    this.clientNavigation$.next(buildNav);

                    let tempNav = structuredClone(buildNav).map(nav => {
                        if (!nav.link.startsWith('/client/')) {
                            nav.link = `/client/${nav.link}`;
                        }
                        return nav;
                    });

                    // console.log('New Client Navigation FullPath:');
                    // console.log(tempNav);
                    this.clientNavigationFullPath$.next(tempNav);
                    this.updateNavigation();
                }
            );
    }
}

export enum RFModule {
    Admin = 'Admin',
    Client = 'Client',
    Default = 'Default'
}
